import { InformationCircleIcon } from "@heroicons/react/24/solid";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import Loader from "@/atoms-react/loader/Loader.react";
import { Shield } from "@/atoms-react/shield/index.react";
import { Base } from "@/atoms-react/text/Text.react";
import { MiniAnimatedShield } from "@/components/animations/minishield/index.react";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { companyDetails } from "@/features/company/types";
import type { levelBronzeFormDataType } from "@/features/flow/types";
import { useKYC } from "@/features/kyc/hooks/use-kyc";
import { KYCStatusType } from "@/features/kyc/types";
import type { bronzeCategories } from "@/features/user/quiz/types";
import type { UserType } from "@/features/user/types";
import { ModalLevelObtain } from "@/molecules/modal-level/index.react";
import { isHttpUrlValid } from "../level-bronze-quiz/bronze-form/utils";
import { LebelQuizBronze } from "../level-bronze-quiz/index.react";
import { LevelDocuments } from "../level-document/index.react";
import { LevelOlkyAccount } from "../level-olky-account/index.react";
import { LevelOlkyAccountSilverOR } from "../level-olky-account-silver-or/index.react";
import { AuthenticatedCompanyForm } from "./authenticated-company-form/authenticated-company-form";
import { DeclarativeCompanyForm } from "./declarative-company-form/declarative-company-form";
import { DeclarativeForm } from "./declarative-form/declarative-form";
import { VerifiedCompanyForm } from "./verified-company-form/verified-company-form";

export type LevelProps = {
  status: KYCStatusType;
  auditPending?: boolean;
  nextStatus: KYCStatusType | null;
  kycLevel?: number;
  user?: UserType;
  isLoadingUser?: boolean;
  companyDetails?: companyDetails | null;
  isCompanyDataFetching?: boolean;
  actionEnabled: boolean;
  olkyPassInfo: {
    id: number;
    title: string;
    description: string;
    description1?: string;
    description2?: string;
    description3?: string;
    description4?: string;
    sub_description: string;
    description_pro: string;
    subdescription_pt2: string;
    kyc: KYCStatusType;
    lvl: number;
  }[];
  isAdmin: boolean;
  refetchCompany?: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<companyDetails | null, null>>;

  refetchUser?: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<UserType, Error>>;
};
export const LevelCard = ({
  status,
  kycLevel,
  auditPending,
  nextStatus,
  user,
  companyDetails,
  isLoadingUser = false,
  isCompanyDataFetching,
  olkyPassInfo,
  actionEnabled,
  refetchUser,
  isAdmin = false
}: LevelProps) => {
  const { t } = useTranslation(["pass"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectToLvl = searchParams.get("level_to_complete");
  const [open, setOpen] = useState<boolean>(false);
  const [pourcentage, setPourcentage] = useState<number>(0);
  const [allowSubmit, setAllowSubmit] = useState<boolean>(false);
  const { editKycBronzeMutation } = useKYC();
  const { mutateAsync: editForm } = editKycBronzeMutation();
  const { leUuid } = useCompanyContext();
  const { completeCompanyDataMutation, editIdentity3IdentityMutation } = useCompany();

  const { mutateAsync: editCompanyDataLvl3 } = editIdentity3IdentityMutation();
  completeCompanyDataMutation();
  // kycLevel = 4
  const [statusClicked, setStatusClicked] = useState<KYCStatusType>();

  // Fetch and keep bronzeForm state
  const { getKycBronzeFormQuery } = useKYC();
  const { data: bronzeFormData, isLoading } = getKycBronzeFormQuery(
    !leUuid && !!kycLevel && kycLevel >= 3
  );
  const [bronzeFormResponses, setBronzeFormResponses] = useState<levelBronzeFormDataType>();

  useEffect(() => {
    if (bronzeFormData && bronzeFormData.success && (kycLevel as number) >= 3) {
      if (bronzeFormData.data.monthly_income) delete bronzeFormData.data["monthly_income"];

      setBronzeFormResponses({
        ...bronzeFormData.data,
        assets_category:
          bronzeFormData.data.assets_category.length > 0
            ? bronzeFormData.data.assets_category.map((ac) => ac.code.toString())
            : [],
        origin_assets_category:
          bronzeFormData.data.origin_assets_category.length > 0
            ? (bronzeFormData.data.origin_assets_category as bronzeCategories[]).map((ac) =>
                ac.code.toString()
              )
            : [],
        other_assets_category:
          bronzeFormData.data.other_assets_category.length > 0
            ? (bronzeFormData.data.other_assets_category as bronzeCategories[]).map((ac) =>
                ac.code.toString()
              )
            : [],
        other_real_estate_assets:
          bronzeFormData.data.other_real_estate_assets.length > 0
            ? (bronzeFormData.data.other_real_estate_assets as bronzeCategories[]).map((ac) =>
                ac.code.toString()
              )
            : []
      });
    }
  }, [bronzeFormData]);

  useEffect(() => {
    if (bronzeFormResponses) {
      const requiredFields: string[] = [];
      for (const [key, value] of Object.entries(bronzeFormResponses as levelBronzeFormDataType)) {
        if (
          key === "address_justification" ||
          key === "real_estate_assets" ||
          key === "civilState"
        ) {
          if (value !== null) {
            requiredFields.push(key);
          }
        } else if (
          (key === "honor_declaration" && value === true) ||
          (key === "value_real_estate" && value !== null && value !== "0" && value !== "")
        )
          requiredFields.push(key);
      }

      const isLinkedinValid =
        !bronzeFormResponses.linkedin_url || isHttpUrlValid(bronzeFormResponses.linkedin_url);

      if (
        isLinkedinValid &&
        bronzeFormResponses.address_justification !== null &&
        bronzeFormResponses.civilState !== null &&
        (bronzeFormResponses.curriculum_algoreg_session_uuid ||
          bronzeFormResponses.linkedin_url ||
          bronzeFormResponses.career_path) &&
        ((bronzeFormResponses.real_estate_assets !== null &&
          bronzeFormResponses.real_estate_assets === "I own one or more properties" &&
          bronzeFormResponses.value_real_estate !== "0" &&
          bronzeFormResponses.value_real_estate !== null &&
          bronzeFormResponses.value_real_estate !== "") ||
          bronzeFormResponses.real_estate_assets === "I don't own a property") &&
        bronzeFormResponses.honor_declaration
      ) {
        handleProgressBar(100);
        setAllowSubmit(true);
      } else if (requiredFields.length === 0) {
        handleProgressBar(0);
        setAllowSubmit(false);
      } else {
        if (
          requiredFields.includes("real_estate_assets") &&
          bronzeFormResponses.real_estate_assets === "I own one or more properties"
        ) {
          handleProgressBar((requiredFields.length * 100) / 5);
        } else handleProgressBar((requiredFields.length * 100) / 4);

        setAllowSubmit(false);
      }
    }
  }, [bronzeFormResponses]);

  const handleProgressBar = (pourcenteage: number) => {
    setPourcentage(pourcenteage);
  };

  const getMiniShield = () => {
    switch (status) {
      case KYCStatusType.DECLARATIVE:
        return (
          <MiniAnimatedShield style="bottom-14 left-4 w-5 h-3" colors="#D11E1F" percentage={45} />
        );

      case KYCStatusType.VERIFIED:
        return (
          <MiniAnimatedShield style="bottom-14 left-3 w-6 h-3" colors="#1D98DF" percentage={50} />
        );

      case KYCStatusType.AUTHENTICATED:
        return (
          <MiniAnimatedShield style="bottom-14  left-2 w-8 h-4" colors="#82C805" percentage={60} />
        );

      case KYCStatusType.AUTHENTICATED_BRONZE:
        return (
          <MiniAnimatedShield colors="#652E10" style="top-16 left-1 w-10 h-4" percentage={70} />
        );
      case KYCStatusType.AUTHENTICATED_SILVER:
        return (
          <MiniAnimatedShield colors="#B7B7B7" style="top-16 left-1 w-10 h-4" percentage={78} />
        );

      case KYCStatusType.AUTHENTICATED_GOLD:
        return (
          <MiniAnimatedShield colors="#D09A31" style="top-16 left-1 w-10 h-4" percentage={100} />
        );
      default:
        break;
    }
  };
  const handleEdit = (formBronze: levelBronzeFormDataType) => {
    try {
      editForm(formBronze);
      setBronzeFormResponses(formBronze);
    } catch (error) {
      console.log(error);
    }
  };
  const getContent = (statusClicked: KYCStatusType) => {
    const level = getLevel(statusClicked);

    if (auditPending && level !== 1 && level !== 3) return;

    if (leUuid && (level > 3 || !companyDetails?.legalEntity)) return;

    if ((kycLevel as number) < level || level === 1 || level === 2 || level === 3) {
      switch (statusClicked) {
        case KYCStatusType.AUTHENTICATED_GOLD:
          return !leUuid && <LevelOlkyAccountSilverOR statusKyc={statusClicked} />;

        case KYCStatusType.AUTHENTICATED_SILVER:
          return !leUuid && <LevelOlkyAccountSilverOR statusKyc={statusClicked} />;

        case KYCStatusType.AUTHENTICATED_BRONZE:
          return (
            !leUuid && (
              <>
                <LebelQuizBronze
                  user={user}
                  status={kycLevel as number}
                  handlePourcentage={(pourcenteage: number) => {
                    setPourcentage(pourcenteage);
                  }}
                  handleEdit={handleEdit}
                  bronzForm={bronzeFormResponses as levelBronzeFormDataType}
                  allowSubmit={allowSubmit}
                />
              </>
            )
          );

        case KYCStatusType.AUTHENTICATED:
          return leUuid && !!companyDetails ? (
            <AuthenticatedCompanyForm
              company={companyDetails}
              isAdmin={isAdmin}
              actionEnabled={actionEnabled}
              editCompanyData={async (data) => {
                try {
                  await editCompanyDataLvl3({
                    data,
                    id: leUuid
                  });
                  toast.success(t("olkypass.accountPage.notifications.success.profile.update"));
                } catch (_) {
                  toast.error(t("olkypass.accountPage.notifications.error.profile.update"));
                }
              }}
            />
          ) : (
            <LevelOlkyAccount
              refetchUser={refetchUser}
              isLoadingUser={isLoadingUser}
              user={user as UserType}
            ></LevelOlkyAccount>
          );

        case KYCStatusType.VERIFIED:
          return leUuid && !!companyDetails ? (
            <VerifiedCompanyForm
              companyDetails={companyDetails}
              tooltipContent={getTooltip(status)}
              isAdmin={isAdmin}
              actionEnabled={actionEnabled}
            />
          ) : (
            <LevelDocuments />
          );

        case KYCStatusType.DECLARATIVE:
          return leUuid && !!companyDetails ? (
            <DeclarativeCompanyForm
              companyDetails={companyDetails ?? undefined}
              isCompanyDataFetching={!!isCompanyDataFetching}
              isAdmin={isAdmin}
              actionEnabled={actionEnabled}
            />
          ) : (
            <DeclarativeForm />
          );
        default:
          break;
      }
    }
  };
  const getName = (statusClicked: KYCStatusType) => {
    switch (statusClicked) {
      case KYCStatusType.AUTHENTICATED_GOLD:
        return olkyPassInfo[5].title;

      case KYCStatusType.AUTHENTICATED_SILVER:
        return olkyPassInfo[4].title;

      case KYCStatusType.AUTHENTICATED_BRONZE:
        return olkyPassInfo[3].title;

      case KYCStatusType.AUTHENTICATED:
        return olkyPassInfo[2].title;

      case KYCStatusType.VERIFIED:
        return olkyPassInfo[1].title;

      case KYCStatusType.DECLARATIVE:
        return olkyPassInfo[0].title;

      default:
        break;
    }
  };

  const getNameByLevel = (level: number) => {
    switch (level) {
      case 6:
        return KYCStatusType.AUTHENTICATED_GOLD;

      case 5:
        return KYCStatusType.AUTHENTICATED_SILVER;

      case 4:
        return KYCStatusType.AUTHENTICATED_BRONZE;

      case 3:
        return KYCStatusType.AUTHENTICATED;

      case 2:
        return KYCStatusType.VERIFIED;

      case 1:
        return KYCStatusType.DECLARATIVE;

      default:
        break;
    }
  };
  const getTooltip = (status: KYCStatusType): string => {
    switch (status) {
      case KYCStatusType.AUTHENTICATED_GOLD:
        return leUuid ? olkyPassInfo[5].description_pro : olkyPassInfo[5].description;
      case KYCStatusType.AUTHENTICATED_SILVER:
        return leUuid ? olkyPassInfo[4].description_pro : olkyPassInfo[4].description;
      case KYCStatusType.AUTHENTICATED_BRONZE:
        return leUuid ? olkyPassInfo[3].description_pro : olkyPassInfo[3].description;
      case KYCStatusType.AUTHENTICATED:
        return leUuid ? olkyPassInfo[2].description_pro : olkyPassInfo[2].description;
      case KYCStatusType.VERIFIED: {
        if (leUuid) return olkyPassInfo[1].description_pro;
        const htmls = ` <div style="padding:4px;">   <p  style="marging-bottom:10px; marging-bottom:4px;"> ${olkyPassInfo[1].description} </p> 
        <ul style="marging-top:10px; padding:4px;">
                                <li "> 
                                  <p >\u2022 ${olkyPassInfo[1]?.description1}</p>
                                </li>
                                <li>
                                  <p >\u2022 ${olkyPassInfo[1]?.description2}</p>
                                </li>
                                <li>
                                  <p>\u2022 ${olkyPassInfo[1]?.description3}</p>
                                </li>
                              </ul>
                              <p> ${olkyPassInfo[1]?.description4}</p> </div>`;
        return htmls;
      }
      case KYCStatusType.DECLARATIVE:
        return leUuid ? olkyPassInfo[0].description_pro : olkyPassInfo[0].description;
      case KYCStatusType.LOADING:
      case KYCStatusType.NEUTRAL:
        return olkyPassInfo[0].description;
    }
  };
  const getLevel = (status: KYCStatusType): number => {
    switch (status) {
      case KYCStatusType.AUTHENTICATED_GOLD:
        return olkyPassInfo[5].lvl;
      case KYCStatusType.AUTHENTICATED_SILVER:
        return olkyPassInfo[4].lvl;

      case KYCStatusType.AUTHENTICATED_BRONZE:
        return olkyPassInfo[3].lvl;

      case KYCStatusType.AUTHENTICATED:
        return olkyPassInfo[2].lvl;

      case KYCStatusType.VERIFIED:
        return olkyPassInfo[1].lvl;

      case KYCStatusType.DECLARATIVE:
        return olkyPassInfo[0].lvl;

      case KYCStatusType.LOADING:
      case KYCStatusType.NEUTRAL:
        return olkyPassInfo[0].lvl;
    }
  };

  function getDataCyToggleInfosLevel(status: KYCStatusType) {
    return "toggle-infos-level-" + status;
  }

  useEffect(() => {
    if (!redirectToLvl || (status && getLevel(status) !== Number(redirectToLvl))) return;
    if (redirectToLvl === "4" && !bronzeFormData) return;
    if (
      redirectToLvl &&
      kycLevel &&
      (kycLevel === Number(redirectToLvl) - 1 || Number(redirectToLvl) === 1) &&
      !auditPending
    ) {
      setStatusClicked(getNameByLevel(Number(redirectToLvl)));
      setSearchParams((searchParams) => {
        searchParams.delete("level_to_complete");
        return searchParams;
      });
    }
  }, [auditPending, bronzeFormData, redirectToLvl]);
  return (
    <div className="mb-4 border-2 border-slate-50 md:rounded-sm dark:md:border-2 dark:md:border-slate-800">
      <div
        onClick={() => {
          if (isLoading || statusClicked === status) setStatusClicked(undefined);
          else if (
            getLevel(status) <= (kycLevel as number) + 1 ||
            (leUuid && (getLevel(status) === 1 || getLevel(status) === 2 || getLevel(status) === 3))
          )
            setStatusClicked(status);
          else setStatusClicked(undefined);
        }}
        className={twMerge(
          `w-full cursor-pointer rounded-sm bg-bottom bg-cover p-2 text-left ${
            (!leUuid ? getLevel(status) > (kycLevel as number) + 1 : getLevel(status) > 3)
              ? "opacity-25"
              : ""
          }  `
        )}
      >
        {
          <div className="order-1 flex w-full flex-col md:flex-row md:justify-between">
            <div
              className={twMerge(
                `flex w-full flex-start justify-center rounded-sm bg-bottom bg-cover md:w-1/5 lg:px-5`,
                status === KYCStatusType.DECLARATIVE &&
                  "h-[100px] bg-[url('/images/olkypass/declarative_kyc_bg.png')]",
                status === KYCStatusType.AUTHENTICATED_BRONZE &&
                  "h-[100px] bg-[url('/images/olkypass/declarative_authenticted_bronze_kyc_bg.png')]",
                status === KYCStatusType.VERIFIED &&
                  "h-[100px] bg-[url('/images/olkypass/verified_kyc_bg.png')]",
                status === KYCStatusType.AUTHENTICATED &&
                  "h-[100px] bg-[url('/images/olkypass/authenticated_kyc_bg.png')]",
                status === KYCStatusType.AUTHENTICATED_SILVER &&
                  "h-[100px] bg-[url('/images/olkypass/declarative_authenticted_silver_kyc_bg.png')]",
                status === KYCStatusType.AUTHENTICATED_GOLD &&
                  "h-[100px] bg-[url('/images/olkypass/declarative_authenticted_or_kyc_bg.png')]"
              )}
            >
              <div className={"flex flex-col justify-center"}>{getMiniShield()}</div>
            </div>
            <div
              className="mt-2 flex w-full w-full flex-row content-center justify-center md:mt-0 md:ml-4 md:flex-col"
              data-cy={getDataCyToggleInfosLevel(status)}
            >
              <div className="flex flex-row whitespace-nowrap">
                <Base className="font-semibold text-[22px] text-slate-500">{getName(status)}</Base>
                <div className="flex flex-col justify-center">
                  <InformationCircleIcon
                    className="ml-2 h-5 w-5 rounded-full bg-primary text-white dark:text-slate-900"
                    data-tooltip={getTooltip(status)}
                    data-position="top"
                  />
                </div>
              </div>
            </div>
            <div> </div>
            {auditPending && nextStatus && getLevel(status) === getLevel(nextStatus) ? (
              <div className="order-1 flex w-full flex-col justify-center px-4 text-right">
                <div className="mt-4 flex flex-row justify-center md:mt-0 md:justify-end">
                  <Base className="mr-4 font-semibold text-base text-slate-400 underline">
                    {t("olkypass.level.neutral_title")}
                  </Base>
                  <img src={"images/olkypass/orange_shield.svg"} alt="" />
                </div>
              </div>
            ) : (
              <>
                {(kycLevel as number) >= getLevel(status) ? (
                  <div className="order-1 flex w-full flex-col justify-center px-4 text-right">
                    <div className="mt-4 flex flex-row justify-center md:mt-0 md:justify-end">
                      <div className="flex flex-col justify-center">
                        {getLevel(status) === 1 && (
                          <Base className="mr-4 font-semibold text-base text-slate-400">
                            {t("olkypass.general.see_my_info")}
                          </Base>
                        )}
                      </div>
                      {<Shield verified={true} intermediate={false} />}
                    </div>
                  </div>
                ) : (
                  <div className="order-1 flex w-full flex-col justify-center text-right">
                    {getLevel(status) === 4 && !leUuid && (
                      <>
                        {!isLoading ? (
                          <div className="mt-2 flex flex-row justify-end align-right">
                            {pourcentage === 0 ? (
                              <div className="mb-4 flex h-5 w-40 flex-row justify-center rounded-full bg-gray-200 dark:bg-gray-700">
                                <div
                                  className="h-5 max-w-40 rounded-full"
                                  style={{ width: pourcentage + "%" }}
                                >
                                  <Base
                                    className={`flex flex-row justify-center text-sm text-white`}
                                  >
                                    <span>{pourcentage.toFixed(0)}%</span>
                                  </Base>
                                </div>
                              </div>
                            ) : (
                              <div className="relative mb-4 h-5 w-40 rounded-full bg-gray-200 dark:bg-gray-700">
                                <Base
                                  className={`absolute right-0 left-0 ml-2 flex flex-row justify-center text-sm ${
                                    pourcentage === 0 ? "text-primary" : "text-white"
                                  }`}
                                >
                                  <span>{pourcentage.toFixed(0)}%</span>
                                </Base>
                                <div
                                  className="h-5 max-w-40 rounded-full bg-orange-500 dark:bg-orange-500"
                                  style={{ width: pourcentage + "%" }}
                                ></div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="mt-2 flex flex-row justify-end align-center">
                            <Loader className="mr-10"></Loader>
                          </div>
                        )}
                      </>
                    )}
                    {getLevel(status) === 4 && !leUuid && (
                      <Base className="mr-4 font-semibold text-base text-slate-400 underline-offset-1">
                        {t("olkypass.general.finish")}
                      </Base>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        }
      </div>
      {!!statusClicked && <div className="animate-slideIn">{getContent(statusClicked)}</div>}

      <ModalLevelObtain
        statusClicked={statusClicked as KYCStatusType}
        title={getName(statusClicked as KYCStatusType) ?? ""}
        onClose={() => setOpen(false)}
        isOpen={open}
      />
    </div>
  );
};
